import RAPIER from '@dimforge/rapier3d-compat';
import type { BoxCollider } from '../world/plan';

/**
 * THE PHYSICS WORLD.
 *
 * Deliberately thin. The mall is entirely static — a few thousand boxes that
 * never move — and the only dynamic thing in it is a player capsule, so this is
 * a fixed-collider world plus Rapier's kinematic character controller and
 * nothing else. No rigid bodies, no joints, no simulation of dropped objects.
 *
 * Two reasons that matters. First, a kinematic controller is the only way to get
 * movement that feels like a first-person shooter: a dynamic capsule slides down
 * ramps, catches on seams and gets shoved by its own collision impulses, and
 * every game that ships with one spends the rest of its life fighting it.
 * Second, netcode. A kinematic controller is a pure function of (position,
 * desired translation, world) — the host can re-run it for a remote player and
 * get the same answer the client got, which is what makes prediction possible at
 * all. The moment the player is a dynamic body, the whole solver state has to
 * agree across the wire.
 *
 * The world is stepped, but only so the controller's queries see up-to-date
 * broad-phase data; nothing in it has velocity.
 */

/** Rapier ships as WASM and has to be awaited exactly once per page. */
let initialised: Promise<void> | null = null;
export function initPhysics(): Promise<void> {
  if (!initialised) initialised = RAPIER.init();
  return initialised;
}

/**
 * Colliders the character controller must be able to walk through once they
 * open — shutters, service doors. Everything else is welded to the building.
 */
export interface PhysicsHandles {
  world: RAPIER.World;
  /** Removes a barrier's collider. The only runtime change to the world. */
  openBarrier(id: string): void;
  /** Puts one back. Used by the round reset. */
  closeBarrier(id: string): void;
}

export class MallPhysics implements PhysicsHandles {
  readonly world: RAPIER.World;
  private readonly barriers = new Map<string, { collider: RAPIER.Collider | null; def: BoxCollider }>();
  private readonly body: RAPIER.RigidBody;

  constructor(colliders: BoxCollider[], barriers: BoxCollider[]) {
    this.world = new RAPIER.World({ x: 0, y: 0, z: 0 });
    // Everything static hangs off ONE fixed body. Rapier stores a transform per
    // body, not per collider, so a body each for three thousand boxes is three
    // thousand transforms to keep coherent for no benefit — none of them move.
    this.body = this.world.createRigidBody(RAPIER.RigidBodyDesc.fixed());
    for (const c of colliders) this.add(c, this.body);
    for (const c of barriers) {
      const own = this.world.createRigidBody(RAPIER.RigidBodyDesc.fixed());
      this.barriers.set(c.id!, { collider: this.add(c, own), def: c });
    }
    // The ground plane. The zones lay floors but a player who clips past one
    // should land on something rather than fall forever.
    const floor = RAPIER.ColliderDesc.cuboid(200, 0.5, 200).setTranslation(0, -0.5, 0);
    this.world.createCollider(floor, this.body);
  }

  /**
   * Colliders that stop a body but not a photon. See `BoxCollider.sky`.
   *
   * A set of handles rather than a flag read off the collider, because Rapier's
   * user-data is a 128-bit integer and this is the only bit we would ever put in
   * it.
   */
  private readonly glazing = new Set<number>();

  private add(c: BoxCollider, on: RAPIER.RigidBody): RAPIER.Collider {
    const desc = RAPIER.ColliderDesc.cuboid(c.hx, c.hy, c.hz).setTranslation(c.x, c.y, c.z);
    if (c.yaw || c.pitch) {
      // Yaw then pitch, matching how `plan.ts` authors ramps: the stair slab is
      // rotated about Y into the building's frame and then pitched about its own
      // local X. Composing in the other order tilts it sideways.
      const q = yawPitchQuat(c.yaw ?? 0, c.pitch ?? 0);
      desc.setRotation(q);
    }
    const collider = this.world.createCollider(desc, on);
    if (c.sky) this.glazing.add(collider.handle);
    return collider;
  }

  openBarrier(id: string): void {
    const b = this.barriers.get(id);
    if (!b || !b.collider) return;
    this.world.removeCollider(b.collider, false);
    b.collider = null;
  }

  closeBarrier(id: string): void {
    const b = this.barriers.get(id);
    if (!b || b.collider) return;
    const own = this.world.createRigidBody(RAPIER.RigidBodyDesc.fixed());
    b.collider = this.add(b.def, own);
  }

  step(): void {
    this.world.step();
  }

  /**
   * Colliders belonging to people rather than to the building.
   *
   * Every sight line in this game starts inside somebody's own capsule — the
   * player's eye is 1.6m up the middle of theirs, a guard's is the same. An
   * unfiltered ray from there reports a hit at zero distance against the caster,
   * which reads as "something is in the way" for absolutely everything and
   * silently deletes the entire interaction system. Casters register here and
   * are skipped by their own casts.
   */
  private readonly actors = new Set<number>();

  registerActor(c: RAPIER.Collider): void {
    this.actors.add(c.handle);
  }

  /**
   * Capsules belonging to players, as distinct from actors in general.
   *
   * Players do not collide with each other, and that is a decision rather than a
   * shortcut. Four thieves converging on one drop-off station is the busiest
   * moment in the game, and a heist where a teammate can wedge you in a service
   * doorway is a heist that ends in an argument. It also removes an entire class
   * of network bug: with host authority, a body you cannot walk through has to
   * agree between machines, and the frame your prediction says you slipped past
   * your friend and the host says you did not is a frame you get yanked
   * backwards. Guards are deliberately NOT in this set — being physically stopped
   * by the man with the torch is the whole point of him.
   */
  private readonly crowd = new Set<number>();

  registerCrowd(c: RAPIER.Collider): void {
    this.crowd.add(c.handle);
    this.actors.add(c.handle);
  }

  forgetCollider(c: RAPIER.Collider): void {
    this.crowd.delete(c.handle);
    this.actors.delete(c.handle);
  }

  /** Handed to the character controller so a player slides past a teammate. */
  readonly notCrowd = (c: RAPIER.Collider): boolean => !this.crowd.has(c.handle);

  /**
   * First solid thing along a ray, or null. Used for interaction targeting and
   * for the guards' line of sight, so apart from the caster it ignores nothing —
   * if a shutter is shut, you cannot see or reach through it.
   */
  ray(
    from: { x: number; y: number; z: number },
    dir: { x: number; y: number; z: number },
    maxToi: number,
    self?: RAPIER.Collider,
  ): number | null {
    const skip = self ? self.handle : -1;
    const hit = this.world.castRayAndGetNormal(
      new RAPIER.Ray(from, dir),
      maxToi,
      true,
      undefined,
      undefined,
      undefined,
      undefined,
      (c) => c.handle !== skip && !this.actors.has(c.handle),
    );
    return hit ? hit.timeOfImpact : null;
  }

  /**
   * Is there open sky along this ray?
   *
   * The same cast, minus the glazing. Only the exposure sampler uses it: it is
   * how the moon gets into the atrium and onto the plaza while the roof still
   * keeps the player in the building.
   */
  skyRay(from: { x: number; y: number; z: number }, dir: { x: number; y: number; z: number }, maxToi: number): boolean {
    const hit = this.world.castRay(new RAPIER.Ray(from, dir), maxToi, true, undefined, undefined, undefined, undefined, (c) =>
      !this.actors.has(c.handle) && !this.glazing.has(c.handle),
    );
    return hit === null;
  }
}

function yawPitchQuat(yaw: number, pitch: number): RAPIER.Rotation {
  const cy = Math.cos(yaw / 2);
  const sy = Math.sin(yaw / 2);
  const cp = Math.cos(pitch / 2);
  const sp = Math.sin(pitch / 2);
  // q = qYaw * qPitch, with qYaw = (0, sy, 0, cy) and qPitch = (sp, 0, 0, cp).
  return { x: cy * sp, y: sy * cp, z: -sy * sp, w: cy * cp };
}

export { RAPIER };
